import React, { useState, useMemo } from 'react';
import { useData } from '../context/DataContext';
import { Plus, Edit2, Trash2, ChevronDown, TrendingUp, EyeOff, ArrowDownLeft, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { ModalAgregarInversion } from '../components/ModalAgregarInversion';
import { ModalActualizarValor } from '../components/ModalActualizarValor';
import { ModalRetiroInversion } from '../components/ModalRetiroInversion';
import { Inversion } from '../types';
import { TIPO_INVERSION_LABEL, TIPO_INVERSION_ICON } from '../utils/inversionUtils';
import { useConfirmar } from '../context/ConfirmarContext';
import { useLimite } from '../hooks/useLimite';
import { ContadorLimite } from '../components/plus/ContadorLimite';
import { usePlus } from '../context/PlusContext';

// ── Helpers ───────────────────────────────────────────────────────────────────

function etiquetaComision(inv: Inversion): string {
  const c = inv.comision;
  if (c.tipo === 'ninguna')         return 'Sin comisión';
  if (c.tipo === 'porcentaje_fijo') return `${c.porcentajeFijo}% fijo`;
  if (c.tipo === 'escalonada' && c.tramos) {
    return c.tramos.map(t => `${t.porcentaje}%`).join(' → ');
  }
  return '—';
}

function colorRentabilidad(val: number) {
  if (Math.abs(val) < 0.01) return 'text-gray-500 dark:text-gray-400';
  return val > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
}

// ── Componente ────────────────────────────────────────────────────────────────

export const Inversiones: React.FC = () => {
  const confirmar = useConfirmar();
  const limite = useLimite('inversiones');
  const { ofrecerPlus } = usePlus();
  // Inversiones es de Plus: sin él se sigue viendo lo que haya, pero no se crean nuevas
  const sinPlus = limite.maximo === 0;
  const {
    inversiones, aportacionesInversion, retirosInversion, valoracionesInversion,
    cuentas, eliminarInversion, loading,
  } = useData();

  const [showModalCrear, setShowModalCrear]   = useState(false);
  const [invEditar, setInvEditar]             = useState<Inversion | undefined>(undefined);
  const [invActualizar, setInvActualizar]     = useState<Inversion | undefined>(undefined);
  const [invRetirar, setInvRetirar]           = useState<Inversion | undefined>(undefined);
  const [expandedId, setExpandedId]           = useState<string | null>(null);
  const [soloActivas, setSoloActivas]         = useState(true);

  const getNombreCuenta = (id?: string) => cuentas.find(c => c.id === id)?.nombre ?? '—';

  // ── Métricas por inversión ───────────────────────────────────────────────
  const metricas = useMemo(() => {
    return inversiones.map(inv => {
      const aports = aportacionesInversion.filter(a => a.inversionId === inv.id);
      const retiros = retirosInversion.filter(r => r.inversionId === inv.id);
      const totalAportado  = aports.reduce((s, a) => s + a.cantidadAportada, 0);
      const totalComisiones = aports.reduce((s, a) => s + a.comisionAplicada, 0);
      const totalInvertido = aports.reduce((s, a) => s + a.cantidadInvertida, 0);
      const totalRetirado = retiros.reduce((s, r) => s + r.cantidadRetirada, 0);
      const totalAportadoEfectivo = totalAportado - totalRetirado;
      const totalInvertidoEfectivo = totalInvertido - totalRetirado;
      const gananciaInvertido = inv.valorActual - totalInvertidoEfectivo;
      const gananciaAportado  = inv.valorActual - totalAportadoEfectivo;
      const rentSobreInvertido = totalInvertidoEfectivo > 0
        ? (gananciaInvertido / totalInvertidoEfectivo) * 100 : 0;
      const rentSobreAportado  = totalAportadoEfectivo > 0
        ? (gananciaAportado  / totalAportadoEfectivo)  * 100 : 0;

      const valoraciones = valoracionesInversion
        .filter(v => v.inversionId === inv.id)
        .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());

      return {
        inv, aports, retiros, valoraciones,
        totalAportado, totalComisiones, totalInvertido, totalRetirado, totalAportadoEfectivo, totalInvertidoEfectivo,
        gananciaInvertido, gananciaAportado,
        rentSobreInvertido, rentSobreAportado,
      };
    });
  }, [inversiones, aportacionesInversion, retirosInversion, valoracionesInversion]);

  // ── Totales del portfolio ────────────────────────────────────────────────
  const activasMetricas = metricas.filter(m => m.inv.activo);
  const portfolio = useMemo(() => {
    const totalAportado   = activasMetricas.reduce((s, m) => s + m.totalAportado, 0);
    const totalComisiones = activasMetricas.reduce((s, m) => s + m.totalComisiones, 0);
    const totalInvertido  = activasMetricas.reduce((s, m) => s + m.totalInvertido, 0);
    const totalRetirado   = activasMetricas.reduce((s, m) => s + m.totalRetirado, 0);
    const totalAportadoEfectivo = totalAportado - totalRetirado;
    const totalInvertidoEfectivo = totalInvertido - totalRetirado;
    const valorActual     = activasMetricas.reduce((s, m) => s + m.inv.valorActual, 0);
    const gananciaInv     = valorActual - totalInvertidoEfectivo;
    const gananciaAport   = valorActual - totalAportadoEfectivo;
    const rentInv   = totalInvertidoEfectivo > 0 ? (gananciaInv  / totalInvertidoEfectivo) * 100 : 0;
    const rentAport = totalAportadoEfectivo > 0 ? (gananciaAport / totalAportadoEfectivo) * 100 : 0;
    return { totalAportado, totalComisiones, totalInvertido, totalRetirado, totalAportadoEfectivo, totalInvertidoEfectivo, valorActual, gananciaInv, gananciaAport, rentInv, rentAport };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activasMetricas]);

  // ── Agrupación por broker ────────────────────────────────────────────────
  const metricasFiltradas = metricas.filter(m => !soloActivas || m.inv.activo);
  const porBroker = useMemo(() => {
    const mapa = new Map<string, typeof metricasFiltradas>();
    metricasFiltradas.forEach(m => {
      const lista = mapa.get(m.inv.broker) ?? [];
      lista.push(m);
      mapa.set(m.inv.broker, lista);
    });
    return Array.from(mapa.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [metricasFiltradas]);

  const handleEliminar = async (id: string) => {
    if (await confirmar({
      titulo: '¿Eliminar este producto?',
      mensaje: 'Las aportaciones asociadas también se eliminarán.',
      accion: 'Eliminar',
    }))
      try { await eliminarInversion(id); } catch (e) { console.error(e); }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="text-gray-600 dark:text-gray-400">Cargando inversiones…</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-8">
      <div className="max-w-5xl mx-auto">

        {/* Cabecera */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Inversiones</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSoloActivas(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition ${
                soloActivas
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
              }`}>
              <EyeOff className="w-4 h-4" />
              {soloActivas ? 'Ver archivadas' : 'Ocultar archivadas'}
            </button>
            <button
              onClick={() => limite.intentar(() => { setInvEditar(undefined); setShowModalCrear(true); })}
              className="flex items-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 transition text-sm">
              {sinPlus ? <Sparkles className="w-4 h-4" /> : <Plus className="w-4 h-4" />} Nueva <ContadorLimite texto={limite.texto} />
            </button>
          </div>
        </div>

        {sinPlus && (
          <button type="button" onClick={() => ofrecerPlus('inversiones')}
            className="w-full mb-6 flex items-center gap-3 p-3 rounded-xl border border-amber-200 dark:border-amber-800
                       bg-amber-50 dark:bg-amber-900/20 text-left text-sm text-amber-900 dark:text-amber-200">
            <Sparkles className="w-5 h-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <span>
              <strong>Inversiones es de myarinn Plus.</strong>{' '}
              Sigue tu cartera, tus aportaciones y la rentabilidad. Toca para ver Plus.
            </span>
          </button>
        )}

        {/* ── Resumen del portfolio ─────────────────────────────────────── */}
        {activasMetricas.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-6 border-l-4 border-blue-500">
            <h2 className="font-bold text-gray-900 dark:text-white mb-4">Portfolio activo</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              {[
                { label: 'Total aportado (efectivo)',  value: `€${portfolio.totalAportadoEfectivo.toFixed(2)}`,  cls: 'text-gray-900 dark:text-white' },
                { label: 'Comisiones',      value: `-€${portfolio.totalComisiones.toFixed(2)}`, cls: 'text-orange-600 dark:text-orange-400' },
                { label: 'Capital invertido', value: `€${portfolio.totalInvertidoEfectivo.toFixed(2)}`, cls: 'text-gray-900 dark:text-white' },
                { label: 'Valor actual',    value: `€${portfolio.valorActual.toFixed(2)}`,    cls: 'text-blue-600 dark:text-blue-400' },
              ].map(({ label, value, cls }) => (
                <div key={label} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
                  <p className={`text-base font-bold mt-1 ${cls}`}>{value}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              {[
                { label: 'Rentabilidad s/invertido', value: portfolio.rentInv,   ganancia: portfolio.gananciaInv  },
                { label: 'Rentabilidad s/aportado',  value: portfolio.rentAport, ganancia: portfolio.gananciaAport },
              ].map(({ label, value, ganancia }) => (
                <div key={label} className={`rounded-lg p-3 text-center ${
                  ganancia > 0.01 ? 'bg-green-50 dark:bg-green-900/20' : ganancia < -0.01 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-gray-50 dark:bg-gray-700/50'
                }`}>
                  <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
                  <p className={`text-base font-bold mt-1 ${colorRentabilidad(ganancia)}`}>
                    {ganancia >= 0 ? '+' : ''}€{ganancia.toFixed(2)}{' '}
                    <span className="text-sm">({value >= 0 ? '+' : ''}{value.toFixed(2)}%)</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Sin inversiones ──────────────────────────────────────────── */}
        {metricasFiltradas.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-10 text-center text-gray-500 dark:text-gray-400">
            No hay productos de inversión. Crea uno para empezar.
          </div>
        )}

        {/* ── Grupos por broker ─────────────────────────────────────────── */}
        {porBroker.map(([broker, items]) => (
          <div key={broker} className="mb-6">
            <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 px-1">
              {broker}
            </h3>
            <div className="space-y-3">
              {items.map(({ inv, aports, retiros, valoraciones,
                totalAportado, totalComisiones, totalInvertido, totalRetirado, totalAportadoEfectivo, totalInvertidoEfectivo,
                gananciaInvertido, gananciaAportado,
                rentSobreInvertido, rentSobreAportado }) => {

                const isExpanded = expandedId === inv.id;
                const tieneComision = inv.comision.tipo !== 'ninguna';

                // Chart data
                const chartData = [
                  ...(totalInvertido > 0 ? [{ fecha: format(new Date(inv.fechaInicio), 'dd/MM/yy'), valor: totalInvertido, label: 'Inicio' }] : []),
                  ...valoraciones.map(v => ({
                    fecha: format(new Date(v.fecha), 'dd/MM/yy'),
                    valor: v.valorTotal,
                  })),
                ];
                if (inv.valorActual > 0 && (valoraciones.length === 0 || valoraciones[valoraciones.length - 1]?.valorTotal !== inv.valorActual)) {
                  chartData.push({ fecha: 'Hoy', valor: inv.valorActual });
                }

                return (
                  <div key={inv.id}
                    className={`bg-white dark:bg-gray-800 rounded-xl shadow border-l-4 ${
                      !inv.activo ? 'border-gray-300 dark:border-gray-600 opacity-70' :
                      gananciaInvertido > 0 ? 'border-green-500' :
                      gananciaInvertido < 0 ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`}>

                    {/* ─ Cabecera de la tarjeta ─ */}
                    <div className="p-4">
                      {/* Fila superior: nombre + acciones */}
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xl">{TIPO_INVERSION_ICON[inv.tipo]}</span>
                            <span className="font-bold text-gray-900 dark:text-white">{inv.nombre}</span>
                            {inv.ticker && (
                              <span className="text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded">
                                {inv.ticker}
                              </span>
                            )}
                            {!inv.activo && (
                              <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full">Archivada</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {TIPO_INVERSION_LABEL[inv.tipo]} · {etiquetaComision(inv)} · desde {format(new Date(inv.fechaInicio), 'MMM yyyy', { locale: es })}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => setInvActualizar(inv)}
                            title="Actualizar valor"
                            className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition">
                            <TrendingUp className="w-4 h-4" />
                          </button>
                          <button onClick={() => setInvRetirar(inv)}
                            title="Retirar fondos"
                            className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition">
                            <ArrowDownLeft className="w-4 h-4" />
                          </button>
                          <button onClick={() => { setInvEditar(inv); setShowModalCrear(true); }}
                            title="Editar"
                            className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleEliminar(inv.id)}
                            title="Eliminar"
                            className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Fila de métricas */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase font-semibold">Aportado (efectivo)</p>
                          <p className="font-bold text-gray-900 dark:text-white">€{totalAportadoEfectivo.toFixed(2)}</p>
                          {tieneComision && (
                            <p className="text-[10px] text-orange-500">−€{totalComisiones.toFixed(2)} com.</p>
                          )}
                          {totalRetirado > 0 && (
                            <p className="text-[10px] text-green-600 dark:text-green-400">−€{totalRetirado.toFixed(2)} ret.</p>
                          )}
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase font-semibold">Capital invertido</p>
                          <p className="font-bold text-gray-900 dark:text-white">€{totalInvertidoEfectivo.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase font-semibold">Valor actual</p>
                          <p className="font-bold text-blue-600 dark:text-blue-400">€{inv.valorActual.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase font-semibold">Rentabilidad</p>
                          {totalAportadoEfectivo > 0 ? (
                            <>
                              <p className={`font-bold ${colorRentabilidad(gananciaInvertido)}`}>
                                {gananciaInvertido >= 0 ? '+' : ''}€{gananciaInvertido.toFixed(2)}
                              </p>
                              <p className={`text-[10px] ${colorRentabilidad(gananciaInvertido)}`}>
                                {rentSobreInvertido >= 0 ? '+' : ''}{rentSobreInvertido.toFixed(1)}% s/inv
                              </p>
                              {tieneComision && (
                                <p className={`text-[10px] ${colorRentabilidad(gananciaAportado)}`}>
                                  {rentSobreAportado >= 0 ? '+' : ''}{rentSobreAportado.toFixed(1)}% s/aport
                                </p>
                              )}
                            </>
                          ) : (
                            <p className="text-[10px] text-gray-400">Sin aportaciones</p>
                          )}
                        </div>
                      </div>

                      {/* Botón expandir */}
                      <button
                        type="button"
                        onClick={() => setExpandedId(isExpanded ? null : inv.id)}
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 mt-3 transition">
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        {isExpanded ? 'Ocultar detalle' : 'Ver aportaciones e historial'}
                      </button>
                    </div>

                    {/* ─ Detalle expandido ─ */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 dark:border-gray-700 px-4 pb-4 pt-3 space-y-4">

                        {/* Gráfico de evolución */}
                        {chartData.length > 1 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                              Evolución del valor
                            </p>
                            <ResponsiveContainer width="100%" height={120}>
                              <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                                <XAxis dataKey="fecha" tick={{ fontSize: 10 }} />
                                <YAxis
                                  tick={{ fontSize: 10 }}
                                  width={55}
                                  domain={totalInvertido > 0 ? ['dataMin - 100', 'dataMax + 100'] : undefined}
                                  tickFormatter={v => `€${v.toFixed(0)}`} />
                                <Tooltip
                                  formatter={(v: number) => [`€${v.toFixed(2)}`, 'Valor']}
                                  labelStyle={{ fontSize: 11 }} />
                                {totalInvertidoEfectivo > 0 && (
                                  <ReferenceLine
                                    y={totalInvertidoEfectivo}
                                    stroke="#C8794A"
                                    strokeDasharray="4 2"
                                    isFront={false}
                                    label={{ value: 'Capital invertido', fontSize: 9, fill: '#C8794A', position: 'right' }} />
                                )}
                                <Line type="monotone" dataKey="valor" stroke="#6584A0"
                                  dot={{ r: 3 }} strokeWidth={2} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        )}

                        {/* Tabla de aportaciones */}
                        <div>
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                            Aportaciones ({aports.length})
                          </p>
                          {aports.length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-2">
                              Sin aportaciones. Realiza un traspaso a esta inversión.
                            </p>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                    <th className="text-left py-1.5 pr-3">Fecha</th>
                                    <th className="text-right pr-3">Aportado</th>
                                    <th className="text-right pr-3">Comisión</th>
                                    <th className="text-right pr-3">Invertido</th>
                                    <th className="text-left">Cuenta</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                                  {[...aports]
                                    .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime())
                                    .map(ap => (
                                      <tr key={ap.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                        <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-300">
                                          {format(new Date(ap.fecha), 'dd/MM/yyyy', { locale: es })}
                                        </td>
                                        <td className="pr-3 text-right font-semibold text-gray-900 dark:text-white">
                                          €{ap.cantidadAportada.toFixed(2)}
                                        </td>
                                        <td className="pr-3 text-right text-orange-500">
                                          {ap.comisionAplicada > 0
                                            ? `-€${ap.comisionAplicada.toFixed(2)} (${ap.porcentajeComision}%)`
                                            : '—'}
                                        </td>
                                        <td className="pr-3 text-right text-blue-600 dark:text-blue-400 font-semibold">
                                          €{ap.cantidadInvertida.toFixed(2)}
                                        </td>
                                        <td className="text-gray-500 dark:text-gray-400 truncate max-w-[100px]">
                                          {getNombreCuenta(ap.cuentaOrigenId)}
                                        </td>
                                      </tr>
                                    ))
                                  }
                                </tbody>
                                <tfoot>
                                  <tr className="border-t border-gray-200 dark:border-gray-600 font-bold text-gray-900 dark:text-white">
                                    <td className="py-1.5 pr-3">Total</td>
                                    <td className="pr-3 text-right">€{totalAportado.toFixed(2)}</td>
                                    <td className="pr-3 text-right text-orange-500">
                                      {totalComisiones > 0 ? `-€${totalComisiones.toFixed(2)}` : '—'}
                                    </td>
                                    <td className="pr-3 text-right text-blue-600 dark:text-blue-400">
                                      €{totalInvertido.toFixed(2)}
                                    </td>
                                    <td />
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          )}
                        </div>

                        {/* Tabla de retiros */}
                        <div>
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                            Retiros ({retiros.length})
                          </p>
                          {retiros.length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-2">
                              Sin retiros. Puedes retirar fondos cuando lo necesites.
                            </p>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                    <th className="text-left py-1.5 pr-3">Fecha</th>
                                    <th className="text-right pr-3">Retirado</th>
                                    <th className="text-left">Cuenta Destino</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                                  {[...retiros]
                                    .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime())
                                    .map(ret => (
                                      <tr key={ret.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                        <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-300">
                                          {format(new Date(ret.fecha), 'dd/MM/yyyy', { locale: es })}
                                        </td>
                                        <td className="pr-3 text-right font-semibold text-green-600 dark:text-green-400">
                                          €{ret.cantidadRetirada.toFixed(2)}
                                        </td>
                                        <td className="text-gray-500 dark:text-gray-400 truncate max-w-[100px]">
                                          {getNombreCuenta(ret.cuentaDestinoId)}
                                        </td>
                                      </tr>
                                    ))
                                  }
                                </tbody>
                                {retiros.length > 0 && (
                                  <tfoot>
                                    <tr className="border-t border-gray-200 dark:border-gray-600 font-bold text-gray-900 dark:text-white">
                                      <td className="py-1.5 pr-3">Total</td>
                                      <td className="pr-3 text-right text-green-600 dark:text-green-400">
                                        €{totalRetirado.toFixed(2)}
                                      </td>
                                      <td />
                                    </tr>
                                  </tfoot>
                                )}
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Modales */}
      {showModalCrear && (
        <ModalAgregarInversion
          onClose={() => { setShowModalCrear(false); setInvEditar(undefined); }}
          inversionEditar={invEditar}
        />
      )}
      {invActualizar && (
        <ModalActualizarValor
          inversion={invActualizar}
          onClose={() => setInvActualizar(undefined)}
        />
      )}
      {invRetirar && (
        <ModalRetiroInversion
          inversion={invRetirar}
          onClose={() => setInvRetirar(undefined)}
        />
      )}
    </div>
  );
};
